import type { JSX } from 'react';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Form, Input } from 'antd';
import { LockOutlined, PhoneOutlined } from '@ant-design/icons';

import { authApi } from '../lib/authApi';
import { ApiError } from '../lib/api';
import { HubLogo } from '../components/HubLogo';

/**
 * Страница входа на wiki.brasol.ru для сотрудников iiko (вид HUB 2.0: логотип
 * fishka HUB, светлая карточка, поле 48 px для телефона).
 *
 * Партнёры CRM сюда обычно не попадают — у них cookie с .brasol.ru
 * уже валидна. Если попали (например в инкогнито) — баннер «Партнёр?
 * Войдите в CRM» направляет на crm.brasol.ru/login.
 *
 * После успешного login:
 *   - mustChangePassword=true → редирект на /change-password
 *   - иначе → редирект на /  (или ?next= если был)
 */
export function LoginPage(): JSX.Element {
  const [params] = useSearchParams();
  const next = params.get('next') || '/';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const loginMutation = useMutation({
    mutationFn: ({ phone, password }: { phone: string; password: string }) =>
      authApi.login(phone, password),
    onSuccess: (data) => {
      setErrorMsg(null);
      qc.invalidateQueries({ queryKey: ['wiki-auth', 'me'] });
      if (data.mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        navigate(next, { replace: true });
      }
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        if (e.status === 401) {
          setErrorMsg('Неверный телефон или пароль');
        } else if (e.status === 403) {
          setErrorMsg(e.message || 'Доступ заблокирован');
        } else {
          setErrorMsg(e.message);
        }
      } else {
        setErrorMsg((e as Error).message);
      }
    },
  });

  return (
    <div className="fk-hub-auth">
      <div className="fk-hub-auth-card">
        <div className="fk-hub-auth-head">
          <HubLogo size="lg" />
          <div>
            <h1>Вход для сотрудников</h1>
            <p>Телефон и пароль, который выдал управляющий</p>
          </div>
        </div>

        {errorMsg && (
          <Alert type="error" message={errorMsg} showIcon style={{ marginBottom: 16 }} closable onClose={() => setErrorMsg(null)} />
        )}

        <Form layout="vertical" onFinish={(v) => loginMutation.mutate({ phone: v.phone, password: v.password })} requiredMark={false}>
          <Form.Item label="Телефон" name="phone" rules={[{ required: true, message: 'Введите телефон' }]}>
            <Input
              size="large"
              inputMode="tel"
              prefix={<PhoneOutlined style={{ color: 'var(--fk-text-muted)' }} />}
              placeholder="+7 999 123-45-67"
              autoComplete="username"
              autoFocus
            />
          </Form.Item>
          <Form.Item label="Пароль" name="password" rules={[{ required: true, message: 'Введите пароль' }]}>
            <Input.Password
              size="large"
              prefix={<LockOutlined style={{ color: 'var(--fk-text-muted)' }} />}
              placeholder="Пароль от управляющего"
              autoComplete="current-password"
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" size="large" block loading={loginMutation.isPending} style={{ fontWeight: 700, height: 48 }}>
            Войти
          </Button>
        </Form>

        <div className="fk-hub-auth-note">
          <b style={{ color: 'var(--fk-info)' }}>Партнёр или сотрудник УК?</b> Войдите в{' '}
          <a href={`https://crm.brasol.ru/login?next=${encodeURIComponent('https://wiki.brasol.ru' + next)}`} style={{ fontWeight: 700 }}>
            CRM
          </a>{' '}
          — после этого HUB откроется автоматически.
        </div>

        <div className="fk-hub-auth-foot">Пароль выдаёт управляющий точки в CRM. Если потеряли — попросите выдать новый.</div>
      </div>
    </div>
  );
}
